import React, { useMemo } from "react";

import { ColumnsType } from "antd/es/table";
import Tag from "antd/es/tag";
import { FormattedMessage, useIntl } from "react-intl";

import IntegrationService from "@mapmycustomers/shared/enum/integrations/IntegrationService";
import IntegrationSyncDirection, {
  getSyncDirection,
} from "@mapmycustomers/shared/enum/integrations/IntegrationSyncDirection";

import Table from "@app/component/Table";
import TextWithLine from "@app/component/typography/TextWithLine";
import { ServiceDescription } from "@app/store/integrations/supported-services";
import Integration from "@app/types/integrations/Integration";
import IntegrationSync from "@app/types/integrations/IntegrationSync";
import { formatRawDate } from "@app/util/formatters";
import { getEntityTypeDisplayName } from "@app/util/ui";

import IntegrationCard from "../IntegrationCard";
import SyncDirection from "../SyncDirection";

interface Props {
  header?: React.ReactNode;
  integrationSync?: IntegrationSync;
  integrationSyncOptions?: Integration["syncOptions"];
  integrationType?: IntegrationService;
  isRealIntegration: boolean;
  selectedService?: ServiceDescription;
}

interface EntityStatus {
  enabled: boolean;
  entityName: string;
  lastSync: string;
}

const IntegrationDetails: React.FC<Props> = ({
  header,
  integrationSync,
  integrationSyncOptions,
  integrationType,
  isRealIntegration,
  selectedService,
}) => {
  const intl = useIntl();

  const resultColumns: ColumnsType<EntityStatus> = useMemo(
    () => [
      {
        dataIndex: "entityName",
        render: (entityName) => <Tag color="processing">{entityName}</Tag>,
        title: intl.formatMessage({
          id: "integrations.details.column.recordType",
          defaultMessage: "Record type",
          description: "Title of a record type field",
        }),
      },
      {
        dataIndex: "lastSync",
        title: intl.formatMessage({
          id: "integrations.details.column.lastSync",
          defaultMessage: "Last  sync",
          description: "Title of a last sync field",
        }),
      },
      {
        dataIndex: "type",
        render: (type) => <SyncDirection direction={type.direction} type={type.integrationType} />,
        title: intl.formatMessage({
          id: "integrations.details.column.direction",
          defaultMessage: "Direction",
          description: "Title of a direction field",
        }),
      },
      {
        dataIndex: "enabled",
        render: (enabled) => {
          return (
            <div>
              <Tag color={enabled ? "success" : undefined}>
                <FormattedMessage
                  id="integrations.details.column.status.text"
                  defaultMessage="{enabled, select, true {Enabled} other {Disabled}}"
                  description="Text of status message"
                  values={{ enabled }}
                />
              </Tag>
            </div>
          );
        },
        title: intl.formatMessage({
          id: "integrations.details.column.status",
          defaultMessage: "Status",
          description: "Title of a status field",
        }),
      },
    ],
    [intl]
  );

  const syncData = useMemo(() => {
    return selectedService?.supportedEntityTypes
      .map((entityType) => ({
        enabled:
          integrationSyncOptions && integrationSyncOptions[entityType]
            ? integrationSyncOptions[entityType].incoming ||
              integrationSyncOptions[entityType].outgoing
            : false,

        entityName: getEntityTypeDisplayName(intl, entityType, {
          lowercase: false,
          plural: true,
        }),
        lastSync: formatRawDate(integrationSync?.updatedAt, "PPPp", "-"),
        type: {
          direction: integrationSyncOptions
            ? getSyncDirection(integrationSyncOptions[entityType])
            : IntegrationSyncDirection.SERVICE_TO_MMC,
          integrationType: integrationType,
        },
      }))
      .sort((a, b) => a.entityName.localeCompare(b.entityName));
  }, [integrationType, integrationSyncOptions, integrationSync, selectedService, intl]);

  return (
    <div>
      {header || (
        <TextWithLine>
          <FormattedMessage
            id="integrations.details.title"
            defaultMessage="Integration details"
            description="Text of Integration details section"
          />
        </TextWithLine>
      )}
      {isRealIntegration && (
        <IntegrationCard>
          <Table
            columns={resultColumns}
            dataSource={syncData}
            noHover
            pagination={false}
            rowKey="entityName"
          />
        </IntegrationCard>
      )}
    </div>
  );
};

export default IntegrationDetails;
